import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const RoleContext = createContext();

export function useRole() {
  return useContext(RoleContext);
}

// Define roles and their hierarchy
export const ROLES = {
  ADMIN: 'ADMIN',
  CUSTOMER: 'CUSTOMER',
  PRO_CUSTOMER: 'PRO_CUSTOMER'
};

// Define role-based permissions
// NOTE: Currently restricted for non-admin users - they only have access to NJ counties map
export const PERMISSIONS = {
  [ROLES.ADMIN]: [
    'manage_users',
    'view_analytics',
    'manage_properties',
    'view_properties',
    'manage_favorites',
    'manage_subscriptions',
    'access_dashboard',
    'access_profile'
  ],
  [ROLES.PRO_CUSTOMER]: [
    // Temporarily restricted - will be enabled in future
    // 'view_properties',
    // 'manage_favorites',
    // 'view_full_listings',
    // 'view_property_details',
    // 'export_data',
    // 'access_dashboard',
    // 'access_profile'
  ],
  [ROLES.CUSTOMER]: [
    // Temporarily restricted - will be enabled in future
    // 'view_properties',
    // 'manage_favorites',
    // 'access_dashboard',
    // 'access_profile'
  ]
};

// For development/testing - set admin emails here
const ADMIN_EMAILS = [
  'testuser@example.com'
];

export function RoleProvider({ children }) {
  const [userRole, setUserRole] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser, loading: authLoading } = useAuth();

  useEffect(() => {
    // Only determine role after auth is done loading
    if (authLoading) {
      return;
    }

    if (!currentUser) {
      setUserRole(null);
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    // Check if user's email is in the admin list
    const isAdmin = ADMIN_EMAILS.includes(currentUser.email);
    
    // Get subscription status from user metadata
    const userMetadata = currentUser.user_metadata || {};
    const appMetadata = currentUser.app_metadata || {};
    const storedRole = userMetadata.role || appMetadata.role;
    const storedSubscription = userMetadata.subscription || appMetadata.subscription;
    
    // Removed debug logs
    
    if (isAdmin) {
      setUserRole(ROLES.ADMIN);
      setSubscriptionStatus('active');
    } else {
      // Check for pro subscription
      const isPro = storedSubscription === 'pro' || storedRole === ROLES.PRO_CUSTOMER;
      setUserRole(isPro ? ROLES.PRO_CUSTOMER : ROLES.CUSTOMER);
      setSubscriptionStatus(isPro ? 'pro' : 'basic');
    }
    
    setLoading(false);
  }, [currentUser, authLoading]);

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!userRole) return false;
    return PERMISSIONS[userRole]?.includes(permission) || false;
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return userRole === role;
  };

  // Check if user is pro (either PRO_CUSTOMER or ADMIN)
  const isPro = () => {
    return hasRole(ROLES.ADMIN) || hasRole(ROLES.PRO_CUSTOMER);
  };

  // Upgrade to pro subscription
  const upgradeToPro = async () => {
    if (!currentUser) {
      throw new Error('User must be logged in to upgrade');
    }

    try {
      // Import admin service dynamically to avoid circular imports
      const { default: adminService } = await import('../services/adminService');
      await adminService.updateUserSubscription(currentUser.id, 'pro');
      
      // Update local state
      setUserRole(ROLES.PRO_CUSTOMER);
      setSubscriptionStatus('pro');
      
      // Removed debug log
      return true;
    } catch (error) {
      console.error('Error upgrading to pro:', error);
      throw error;
    }
  };

  // Update user role (admin only) - integrates with Supabase
  const updateUserRole = async (userId, newRole) => {
    if (!hasRole(ROLES.ADMIN)) {
      throw new Error('Unauthorized: Only admins can update roles');
    }

    try {
      // Import admin service dynamically to avoid circular imports
      const { default: adminService } = await import('../services/adminService');
      await adminService.updateUserRole(userId, newRole);
      // Removed debug log
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  };

  // Update user subscription (admin only)
  const updateUserSubscription = async (userId, subscriptionType) => {
    if (!hasRole(ROLES.ADMIN)) {
      throw new Error('Unauthorized: Only admins can update subscriptions');
    }

    try {
      // Import admin service dynamically to avoid circular imports
      const { default: adminService } = await import('../services/adminService');
      await adminService.updateUserSubscription(userId, subscriptionType);
      // Removed debug log
      return true;
    } catch (error) {
      console.error('Error updating user subscription:', error);
      throw error;
    }
  };

  const value = {
    userRole,
    subscriptionStatus,
    loading,
    hasPermission,
    hasRole,
    isPro,
    upgradeToPro,
    updateUserRole,
    updateUserSubscription,
    ROLES
  };

  return (
    <RoleContext.Provider value={value}>
      {!loading && children}
    </RoleContext.Provider>
  );
} 